import React, { useEffect } from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import { Box, HTMLParser, Image, UI } from '@gik/ui';
import { useTranslation } from 'react-i18next';
import { Analytics } from '@gik/core/utils';
import { useInkind } from '@gik/api/inkinds/inkind';
import GiftBoxOption from './GiftBoxOption';
import { useProducts } from '@gik/checkout';
import { InkindPageViewHistoryItem } from '@gik/inkind-page';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import i18n from '@gik/i18n';

export interface ISendAGiftBoxModalContentProps {
  inkindRouteId: string;
  inkindPageViewHistory: InkindPageViewHistoryItem[];
}

const bem = bemBlock('send-a-gift-box-modal');

export function SendAGiftBoxModalContent({
  inkindRouteId,
  inkindPageViewHistory,
}: ISendAGiftBoxModalContentProps): React.ReactElement {
  const { t } = useTranslation();
  const { data: inkindPage } = useInkind(inkindRouteId);
  const inkindPageUrl = inkindPage?.url;
  const productIds = {
    standard: 182087,
    withGiftCard: 182090,
    deluxe: 182100,
  };

  const { data: products } = useProducts(Object.values(productIds));
  const standardProduct = products?.find(product => product.id === productIds.standard);
  const giftCardProduct = products?.find(product => product.id === productIds.withGiftCard);
  const deluxeProduct = products?.find(product => product.id === productIds.deluxe);
  const imagesDir = `${getAssetsUrl()}inkind-page/img/send-a-gift-box-modal/`;

  useEffect(() => {
    if (inkindPageUrl !== null) {
      Analytics.fireEvent('OpenSendAGiftBoxModal', {
        inkindPageUrl,
      });
    }
  }, [inkindPageUrl]);

  const desktopImages = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
  const mobileImages = ['top', 'mid', 'bottom'];

  return (
    <div className={bem('content-outer')}>
      {desktopImages.map(image => (
        <Image
          key={image}
          className={`${bem(`desktop-bg-${image}`)} gik-hidden-md-down`}
          src={`${imagesDir}desktop-bg-${image}.jpg`}
        />
      ))}
      {mobileImages.map(image => (
        <Image
          key={image}
          className={`${bem(`mobile-bg-${image}`)} gik-hidden-md`}
          src={`${imagesDir}mobile-bg-${image}.jpg`}
        />
      ))}
      <div className={bem('content')}>
        <p className={bem('title')}>{t(translationKeys.SendAGiftboxModalTitle)}</p>
        <p className={bem('page-title')}>{inkindPage?.title}</p>
        <p className={bem('description')}>
          <HTMLParser rawHtml={t(translationKeys.SendAGiftboxModalDescription)} />
        </p>
        <p className={bem('description-2')}>
          <HTMLParser rawHtml={t(translationKeys.SendAGiftboxModalDescription2)} />
        </p>

        <div className={bem('standard-options')}>
          <GiftBoxOption
            product={giftCardProduct}
            title={t(translationKeys.SendAGiftboxModalGiftCardGiftBoxTitle)}
            inkindRouteId={inkindRouteId}
            inkindPageViewHistory={inkindPageViewHistory}
          />
          <div className={bem('or')}>
            <div className={bem('or-line')} />
            <span>or</span>
            <div className={bem('or-line')} />
          </div>
          <GiftBoxOption
            product={standardProduct}
            title={t(translationKeys.SendAGiftboxModalStandardGiftBoxTitle)}
            prepend={t(translationKeys.SendAGiftboxModalStandardGiftBoxPrepend)}
            inkindRouteId={inkindRouteId}
            inkindPageViewHistory={inkindPageViewHistory}
          />
        </div>

        <Box className={bem('deluxe')}>
          <Image className={`${bem(`deluxe-bg-desktop`)} gik-hidden-md-down`} src={`${imagesDir}deluxe-desktop.png`} />
          <Image className={`${bem(`deluxe-bg-mobile`)} gik-hidden-md`} src={`${imagesDir}deluxe-mobile.png`} />
          <GiftBoxOption
            product={deluxeProduct}
            deluxe
            title={t(translationKeys.SendAGiftboxModalDeluxeGiftBoxTitle)}
            append={t(translationKeys.SendAGiftboxModalDeluxeGiftBoxAppend)}
            inkindRouteId={inkindRouteId}
            inkindPageViewHistory={inkindPageViewHistory}
          />
        </Box>
      </div>
    </div>
  );
}

export function openSendAGiftBoxModal(inkindRouteId: string, inkindPageViewHistory: InkindPageViewHistoryItem[]): void {
  UI.dialog(<SendAGiftBoxModalContent inkindRouteId={inkindRouteId} inkindPageViewHistory={inkindPageViewHistory} />, {
    closable: true,
    title: i18n.t(translationKeys.SendAGiftboxModalModalTitle),
    className: bem(),
    autowidth: false,
  });
}
